import { useCallback } from "react";
import { ScrollView } from "react-native";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PressableScale } from "pressto";
import { View, Text, Banner } from "@src/components";
import { Icon } from "@src/components/Icon";
import { LoadingOverlay } from "@src/components/screen-state/LoadingOverlay";
import { ArmDisarmPanelContent } from "@visioni/a3s-alarm-schedules/app";
import { TimePickerWith24Hour } from "@src/components/TimePickerWith24Hour";
import { useUpdateSiteAlarmOverride } from "@src/services/api/hooks";
import { useSiteCameraKeys } from "@src/features/cameras/hooks/useSiteCameraKeys";
import { useSitePermissions } from "@visioni/a3s-access/app";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { makeDateTimeRecord } from "@visioni/schedules";
import { buildAlarmSettingsRoutes } from "@src/features/alarms/utils/alarm-settings-routes";
import type { AlarmException } from "@visioni/a3s-alarm-schedules/app";
import type { AlarmOverride } from "@src/services/api/types";

type ActionParam = "arm" | "disarm";

export default function OverrideSitePage() {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const router = useRouter();
  const { tenantId, siteId, action } = useLocalSearchParams<{
    tenantId: string;
    siteId: string;
    action?: ActionParam;
  }>();
  const { cameraKeys } = useSiteCameraKeys(siteId);
  const { canManageAllAlarmSchedules, isLoading: permissionsLoading } = useSitePermissions(siteId, cameraKeys);
  const permissionsResolved = !permissionsLoading;
  const { headerLeft } = useHeaderBackButton();

  const routes = buildAlarmSettingsRoutes(tenantId, siteId);
  const targetAction: "armed" | "disarmed" | null =
    action === "arm" ? "armed"
    : action === "disarm" ? "disarmed"
    : null;

  const updateSiteOverride = useUpdateSiteAlarmOverride();

  const handleConfirm = useCallback(
    (exception: AlarmException) => {
      if (!targetAction) return;

      const startDate = new Date(
        exception.period.start.year,
        exception.period.start.month - 1,
        exception.period.start.day,
        exception.period.start.hour,
        exception.period.start.minute,
      );
      const endDate = new Date(
        exception.period.end.year,
        exception.period.end.month - 1,
        exception.period.end.day,
        exception.period.end.hour,
        exception.period.end.minute,
      );

      const override: AlarmOverride = {
        id: `site-override-${String(Date.now())}`,
        reason: `Site-wide ${targetAction === "armed" ? "arm" : "disarm"}`,
        action: targetAction,
        author: null,
        period: {
          start: makeDateTimeRecord(
            startDate.getFullYear(),
            startDate.getMonth() + 1,
            startDate.getDate(),
            startDate.getHours(),
            startDate.getMinutes(),
          ),
          end: makeDateTimeRecord(
            endDate.getFullYear(),
            endDate.getMonth() + 1,
            endDate.getDate(),
            endDate.getHours(),
            endDate.getMinutes(),
          ),
        },
      };

      updateSiteOverride.mutate(
        { siteId, override },
        {
          onSuccess: (data: { camerasUpdated: number }) => {
            const actionText = targetAction === "armed" ? "armed" : "disarmed";
            showSuccessToast({
              message: `Site ${actionText}`,
              description: `Override applied to ${String(data.camerasUpdated)} camera${data.camerasUpdated === 1 ? "" : "s"}`,
            });
            router.back();
          },
          onError: (error) => {
            showErrorToast({ error });
          },
        },
      );
    },
    [siteId, targetAction, updateSiteOverride, router],
  );

  const handleResume = useCallback(() => {
    updateSiteOverride.mutate(
      { siteId, override: null },
      {
        onSuccess: (data: { camerasUpdated: number }) => {
          showSuccessToast({
            message: "Schedule resumed",
            description: `Override cleared on ${String(data.camerasUpdated)} camera${data.camerasUpdated === 1 ? "" : "s"}`,
          });
          router.back();
        },
        onError: (error) => {
          showErrorToast({ error });
        },
      },
    );
  }, [siteId, updateSiteOverride, router]);

  const handleSelectAction = useCallback(
    (selectedAction: ActionParam) => {
      router.push(`${routes.overrideSite}?action=${selectedAction}` as never);
    },
    [router, routes.overrideSite],
  );

  const title =
    targetAction === "armed" ? "Arm site"
    : targetAction === "disarmed" ? "Disarm site"
    : "Override site";

  return (
    <>
      <Stack.Screen options={{ title, headerLeft }} />
      <View style={{ flex: 1, position: "relative" }}>
        <ScrollView
          style={{ flex: 1, backgroundColor: theme.colors.background }}
          contentContainerStyle={{
            paddingHorizontal: theme.spacing["3xl"],
            paddingTop: theme.spacing["4xl"],
            paddingBottom: bottom + theme.spacing["6xl"],
            flexGrow: 1,
          }}
          showsVerticalScrollIndicator={false}
        >
          <Banner
            title={
              !permissionsResolved || canManageAllAlarmSchedules ?
                "Apply a temporary override to all cameras at this site."
              : "You don't have permission to manage alarm schedules for all cameras at this site."
            }
            variant={permissionsResolved && !canManageAllAlarmSchedules ? "warning" : "info"}
          />

          {permissionsResolved && canManageAllAlarmSchedules && (
            <View style={localStyles.content}>
              {targetAction === null && (
                <View gap="3xl">
                  <Text textStyle="sm/regular" color="textMuted">
                    Choose an action to apply to all cameras in this site.
                  </Text>

                  <View
                    gap="md"
                    pointerEvents={updateSiteOverride.isPending ? "none" : "auto"}
                    style={{ opacity: updateSiteOverride.isPending ? 0.5 : 1 }}
                  >
                    <PressableScale onPress={() => handleSelectAction("arm")}>
                      <View style={[localStyles.actionCard, { borderColor: theme.colors.purple[200] }]}>
                        <View style={[localStyles.iconCircle, { backgroundColor: theme.colors.purple[600] }]}>
                          <Icon name="lockClosed" size={20} color={theme.colors.white.default} />
                        </View>
                        <View flex={1}>
                          <Text textStyle="sm/medium">Arm site</Text>
                          <Text textStyle="xs/regular" color="textMuted">
                            Temporarily arm all cameras
                          </Text>
                        </View>
                        <Icon name="chevronRight" size={20} color={theme.colors.textMuted} />
                      </View>
                    </PressableScale>

                    <PressableScale onPress={() => handleSelectAction("disarm")}>
                      <View style={[localStyles.actionCard, { borderColor: theme.colors.warning[400] }]}>
                        <View style={[localStyles.iconCircle, { backgroundColor: theme.colors.warning[600] }]}>
                          <Icon name="lockOpen" size={20} color={theme.colors.white.default} />
                        </View>
                        <View flex={1}>
                          <Text textStyle="sm/medium">Disarm site</Text>
                          <Text textStyle="xs/regular" color="textMuted">
                            Temporarily disarm all cameras
                          </Text>
                        </View>
                        <Icon name="chevronRight" size={20} color={theme.colors.textMuted} />
                      </View>
                    </PressableScale>

                    <PressableScale onPress={handleResume}>
                      <View style={[localStyles.actionCard, { borderColor: theme.colors.borderCard }]}>
                        <View
                          style={[
                            localStyles.iconCircle,
                            { backgroundColor: theme.colors.interactive.controls.default_midground },
                          ]}
                        >
                          <Icon name="calendarRefresh" size={20} color={theme.colors.white.default} />
                        </View>
                        <View flex={1}>
                          <Text textStyle="sm/medium">Resume schedule</Text>
                          <Text textStyle="xs/regular" color="textMuted">
                            Clear all active overrides for cameras in this site
                          </Text>
                        </View>
                        <Icon name="chevronRight" size={20} color={theme.colors.textMuted} />
                      </View>
                    </PressableScale>
                  </View>
                </View>
              )}

              {targetAction !== null && (
                <ArmDisarmPanelContent
                  mode="override"
                  targetAction={targetAction}
                  onConfirm={handleConfirm}
                  scope="site"
                  isSubmitting={updateSiteOverride.isPending}
                  renderTimePicker={({ isVisible, hour, minute, onConfirm, onCancel }) => (
                    <TimePickerWith24Hour
                      isVisible={isVisible}
                      hour={hour}
                      minute={minute}
                      onConfirm={onConfirm}
                      onCancel={onCancel}
                    />
                  )}
                />
              )}
            </View>
          )}
        </ScrollView>
        {updateSiteOverride.isPending && <LoadingOverlay message="Applying override..." />}
      </View>
    </>
  );
}

const localStyles = StyleSheet.create((theme) => ({
  content: {
    marginTop: theme.spacing["4xl"],
  },
  actionCard: {
    flexDirection: "row",
    alignItems: "center",
    padding: theme.spacing["3xl"],
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    backgroundColor: theme.colors.surfaceCard,
    gap: theme.spacing["3xl"],
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
}));
